import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { DAILY_FREE_PACKS } from "@/lib/game-constants";
import { breadcrumbSchema } from "@/lib/schema";

const ChallengeBoard = lazy(() => import("@/components/ChallengeBoard").then((m) => ({ default: m.ChallengeBoard })));

const FAQ: FaqItem[] = [
  {
    q: "How do I get free Palworld booster packs on Pal Stars?",
    a: `Every free Pal Stars account gets ${DAILY_FREE_PACKS} booster packs a day, resetting at 00:00 UTC. On top of that you can earn bonus packs by finishing challenges on this page — follow Pal Stars on TikTok or Instagram, answer database questions like the rarest bred Pal, or hit collection milestones such as a Gem Mint 10 pull or a completed binder page.`,
  },
  {
    q: "Are the free pack rewards given automatically?",
    a: "Yes. As soon as a challenge is verified, the packs are credited to your account balance and can be opened straight away on the pack opening page. There are no codes to redeem and nothing to wait for.",
  },
  {
    q: "Can I claim the same challenge twice?",
    a: "No. Each challenge pays out once per account. Completions are recorded server-side, so resubmitting a finished task returns a friendly \"already earned\" message instead of more packs.",
  },
  {
    q: "How are collection challenges verified?",
    a: "Milestone challenges read your own banked cards and binder pockets on the server — filing 9 cards, completing a color page, pulling a Gem Mint 10, landing an SSP, OSR or SSS chase card, opening 50 packs, or filling all 108 pockets.",
  },
  {
    q: "Where can I see which rewards I have already claimed?",
    a: "Your claim history sits under the challenge board on this page. It lists every completed challenge, how many packs it paid and the exact date it credited, so you can tell at a glance what is still open.",
  },
  {
    q: "Do bonus packs expire?",
    a: "No. Earned bonus packs sit in your balance until you open them, and they are spent only after that day's free allowance is used up.",
  },

];

export const Route = createFileRoute("/rewards")({
  head: () => ({
    meta: [
      { title: "Free Booster Pack Rewards & Challenges — Pal Stars" },
      { name: "description", content: "Earn free Palworld booster packs on Pal Stars: follow on TikTok, answer breeding and mount questions, and hit binder milestones for 1, 5 or 10 packs." },
      { property: "og:title", content: "Earn free Palworld booster packs" },
      { property: "og:description", content: "Challenges that pay out 1, 5 and 10 free Palworld booster packs automatically on Pal Stars." },
      { property: "og:url", content: "https://palstars.com/rewards" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/rewards" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Free Pack Rewards", "/rewards"]]),
faqSchema(FAQ, "https://palstars.com/rewards")],
  }),
  component: RewardsPage,
});

function RewardsPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars rewards</p>
        <h1 className="mt-2 text-3xl font-bold">Earn free Palworld booster packs</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          You already get {DAILY_FREE_PACKS} free booster packs every day with a Pal Stars account. These challenges hand
          out extra packs — 1 for a quick win, 5 for a database or collection challenge, and 10 for the hardcore chases.
          Rewards are granted automatically the moment a challenge verifies, and each one pays out only once.
        </p>
        <p className="mt-3 text-sm">
          <Link to="/binder-rewards" className="font-semibold text-primary hover:underline">
            Binder rewards — track collection milestones and claim them here →
          </Link>
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/palsino" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:brightness-110">
            🎰 Spin Pal Stars Palsino →
          </Link>
          <Link to="/pack-opening" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Open your packs
          </Link>
          <Link to="/binder" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Your collector binder
          </Link>
          <Link to="/daily-puzzle" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Daily Pal Puzzle & streak
          </Link>
          <Link to="/leaderboard" className="rounded-md border px-5 py-2.5 text-sm font-semibold hover:bg-card">
            Weekly leaderboard
          </Link>
        </div>

      </header>

      <Suspense fallback={<section className="glass rounded-2xl p-6"><h2 className="text-xl font-bold">Challenge board</h2><p className="mt-2 text-sm text-muted-foreground">Loading rewards…</p></section>}><ChallengeBoard /></Suspense>

      <FaqSection items={FAQ} heading="Free booster pack rewards FAQ" />
    </div>
  );
}
